import { createApiRef } from '@backstage/core-plugin-api';
import type {
  KnowledgeBuilderTemplateParameters,
  KnowledgeContextFocus,
  KnowledgeDocument,
  KnowledgeMessage,
  KnowledgeMessageAttachment,
  KnowledgeCapabilities,
  KnowledgeSession,
  KnowledgeSessionKind,
  KnowledgeSource,
  KnowledgeValidationResult,
  ProposedDraftChange,
  DefinitionFieldState,
  DefinitionFieldStatus,
  DefinitionFinding,
  DefinitionStateResponse,
} from './types';

export interface CreateKnowledgeSessionRequest {
  templateRef?: string;
  kind?: KnowledgeSessionKind;
  documentSet?: string;
  sourcePath?: string;
  definitionSchemaPath?: string;
  definitionInterviewInstructionsPath?: string;
  valuesTemplatePath?: string;
  scaffolderParameters?: Record<string, unknown>;
  focus?: KnowledgeContextFocus;
  initialDocument?: string;
  agent?: {
    id?: string;
    provider?: string;
    instructionsPath?: string;
    tools?: {
      enabled?: boolean;
    };
  };
}

export interface KnowledgeSessionResponse {
  session: KnowledgeSession;
  documents: KnowledgeDocument[];
  directories?: string[];
  messages: KnowledgeMessage[];
  sources: KnowledgeSource[];
}

export interface SendKnowledgeMessageResponse {
  message: KnowledgeMessage;
  operations: KnowledgeMessage['operations'];
  updatedDocuments: KnowledgeDocument[];
  proposedChange?: ProposedDraftChange;
}

export interface KnowledgeApi {
  getConfig(): Promise<KnowledgeCapabilities>;
  createSession(
    request: CreateKnowledgeSessionRequest,
  ): Promise<KnowledgeSessionResponse>;
  getSession(sessionId: string): Promise<KnowledgeSessionResponse>;
  updateSessionContext(
    sessionId: string,
    request: {
      focus?: KnowledgeContextFocus;
      templateParameters?: KnowledgeBuilderTemplateParameters;
    },
  ): Promise<KnowledgeSession>;
  sendMessage(
    sessionId: string,
    content: string,
    activeDocumentPath?: string,
    agent?: CreateKnowledgeSessionRequest['agent'],
    signal?: AbortSignal,
    attachments?: KnowledgeMessageAttachment[],
  ): Promise<SendKnowledgeMessageResponse>;
  failActiveRuns(sessionId: string, error?: string): Promise<void>;
  listProposedChanges(sessionId: string): Promise<ProposedDraftChange[]>;
  approveProposedChange(
    sessionId: string,
    changeId: string,
    operationIndex?: number,
  ): Promise<{
    change: ProposedDraftChange;
    updatedDocuments: KnowledgeDocument[];
  }>;
  rejectProposedChange(
    sessionId: string,
    changeId: string,
    operationIndex?: number,
  ): Promise<ProposedDraftChange>;
  updateDocument(
    sessionId: string,
    documentId: string,
    content: string,
  ): Promise<KnowledgeDocument>;
  createDocument(
    sessionId: string,
    request: { path: string; title?: string; content?: string },
  ): Promise<KnowledgeDocument>;
  renameDocument(
    sessionId: string,
    documentId: string,
    path: string,
  ): Promise<KnowledgeDocument>;
  renameFolder(
    sessionId: string,
    oldPath: string,
    newPath: string,
  ): Promise<KnowledgeDocument[]>;
  deleteDocument(sessionId: string, documentId: string): Promise<void>;
  deleteFolder(sessionId: string, path: string): Promise<void>;
  acceptDocument(
    sessionId: string,
    documentId: string,
  ): Promise<KnowledgeDocument>;
  revertDocument(
    sessionId: string,
    documentId: string,
  ): Promise<KnowledgeDocument>;
  addUrl(
    sessionId: string,
    request: { title?: string; url: string },
  ): Promise<KnowledgeSource>;
  uploadFile(sessionId: string, file: File): Promise<KnowledgeSource>;
  removeSource(sessionId: string, sourceId: string): Promise<void>;
  validateSession(sessionId: string): Promise<KnowledgeValidationResult>;
  getDefinitionState(sessionId: string): Promise<DefinitionStateResponse>;
  sendInterviewMessage(
    sessionId: string,
    content: string,
    activeDocumentPath?: string,
    agent?: CreateKnowledgeSessionRequest['agent'],
    signal?: AbortSignal,
  ): Promise<
    SendKnowledgeMessageResponse & {
      definitionUpdates: DefinitionFieldState[];
      definitionState?: DefinitionStateResponse;
    }
  >;
  setDefinitionFieldStatus(
    sessionId: string,
    sectionId: string,
    fieldId: string,
    status: DefinitionFieldStatus,
    value?: string,
  ): Promise<DefinitionFieldState>;
  runDefinitionReview(
    sessionId: string,
  ): Promise<{ findings: DefinitionFinding[]; message: string }>;
}

export const knowledgeApiRef = createApiRef<KnowledgeApi>({
  id: 'plugin.knowledge.service',
});
